/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
// Object.values = Object.values || (obj) => Object.keys(obj).map((key) => obj[key]);

class ApiFactory {
  constructor() {
    this.init();
  }

  init() {
    this.oldAvailableApis = {};
    this.availableApis = {};
    this.oldPendingApiCallbacks = {};
    this.pendingApiCallbacks = {};
  }

  reset() {
    this.init();
  }

  /**
   * getExposedApis Returns a Promise that resolves to an object containing
   * the requested APIs all the requested APIs
   *
   * @param  {string}	clientName Name of the client which needs the API.
   * Useful if some client specific handling is needed.
   *
   * @param  {Array}	requestedApiList List of APIs that should be present in
   * the resolved object
   *
   * @param {Boolean} shouldWaitForAPIs Whether the API Object being returned
   * should be returned immediately or when all the requested APIs are
   * available. If this is false, the returned object contains all API keys
   * as promises.
   *
   * @return {Promise} A promise that resolves to an object containing the
   * requested APIs either as promises or as immediately callable APIs
   */
  getExposedApis(clientName, requestedApiList, shouldWaitForAPIs = true, isNew = false) {
    return new Promise((resolve, reject) => {
      let apiObject = {};
      let apiPromises = this._setupCallbacksForApis(clientName, requestedApiList, shouldWaitForAPIs, isNew);

      if (shouldWaitForAPIs) {
        let promise = Promise.all(Object.values(apiPromises));

        promise.then(apiData => {
          apiData.forEach(api => {
            let obj = {
              [api.apiName]: api.apiGetter(clientName)
            };
            apiObject = Object.assign(apiObject, obj);
          });
          resolve(apiObject);
        });
      } else {
        resolve(apiPromises);
      }
    });
  }

  getAvailableApis(isNew = false) {
    if (isNew) return this.availableApis;
    return this.oldAvailableApis;
  }

  getPendingApiCallbacks(isNew = false) {
    if (isNew) return this.pendingApiCallbacks;
    return this.oldPendingApiCallbacks;
  }

  registerApi(apiName, apiGetter, isNew = false) {
    /**
     * Store as part of APIs that are already available.
     */
    const availableApis = this.getAvailableApis(isNew);
    const pendingApiCallbacks = this.getPendingApiCallbacks(isNew);

    availableApis[apiName] = { apiGetter, apiName };

    if (pendingApiCallbacks[apiName] !== undefined) {
      /**
       * The API has been requested by someone before, call their stored
       * callbacks to provide them the APIs
       */
      pendingApiCallbacks[apiName].forEach(callback => {
        if (typeof callback === "function") {
          callback({ apiGetter, apiName });
        }
      });
    }
  }

  _setupCallbacksForApis(clientName, requestedApiList, needApiName, isNew) {
    let apiObject = {};
    requestedApiList.forEach(api => {
      let promise;
      const availableApis = this.getAvailableApis(isNew);
      const pendingApiCallbacks = this.getPendingApiCallbacks(isNew);

      if (availableApis[api] === undefined) {
        promise = new Promise((resolve, reject) => {
          pendingApiCallbacks[api] = pendingApiCallbacks[api] || [];
          pendingApiCallbacks[api].push(apiData => {
            if (needApiName) {
              resolve(apiData);
            } else {
              resolve(apiData.apiGetter(clientName));
            }
          });
        });
      } else {
        promise = Promise.resolve(availableApis[api]);
      }
      apiObject[api] = promise;
    });

    return apiObject;
  }
}

const apiFactory = new ApiFactory();

export { apiFactory };
